import { createFileRoute, Link, useRouter } from "@tanstack/react-router";
import { useEffect, useMemo, useState, type CSSProperties, type FormEvent } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  ArrowRight,
  Check,
  Copy,
  ExternalLink,
  LayoutGrid,
  ListTree,
  MonitorCog,
  RotateCcw,
  Rows3,
  SquareSplitHorizontal,
} from "lucide-react";
import { AdminListShell } from "@/components/admin/AdminListShell";
import { siteSettingsQuery, INVALIDATE } from "@/lib/query-registry";
import { useAuthedMutation } from "@/lib/authed-mutation";
import { updateThemeColors } from "@/lib/site-settings.functions";
import {
  DEFAULT_THEME_COLORS,
  THEME_BACKGROUND_FIELDS,
  THEME_COLOR_FIELDS,
  THEME_ENGAGEMENT_GAPS,
  THEME_ENGAGEMENT_LAYOUTS,
  THEME_ENGAGEMENT_RADII,
  THEME_ENGAGEMENT_SPACING,
  THEME_GRADIENT_ANGLES,
  THEME_PRESETS,
  ThemeColorsSchema,
  normalizeThemeColors,
  type ThemeAngleKey,
  type ThemeColorKey,
  type ThemeColors,
  type ThemeGradientAngle,
} from "@/lib/theme-settings";

export const Route = createFileRoute("/admin/settings/theme")({
  head: () => ({
    meta: [
      { title: "Theme · Settings · Admin · Shugert" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: ThemePage,
});

type EditorTab = "Home" | "Services" | "Money pages" | "Global";

const TABS: ReadonlyArray<{ value: EditorTab; label: string; description: string }> = [
  { value: "Home", label: "Home", description: "Every homepage section, its surface and its borders." },
  { value: "Services", label: "Services", description: "Services-hub surfaces and visual rhythm." },
  { value: "Money pages", label: "Money pages", description: "Exact template recipes, including Shopify Plus." },
  { value: "Global", label: "Global", description: "Typography, actions and shared semantic colors." },
];

const HOME_SECTIONS = [
  { number: 1, name: "Hero", control: "Own background · Home hero", tone: "Configurable" },
  { number: 2, name: "Proof rail", control: "Warm editorial surface + Home dividers", tone: "Inherited" },
  { number: 3, name: "Client logos", control: "Neutral page background", tone: "Inherited" },
  { number: 4, name: "Press coverage", control: "Cool surface + Press logo plate", tone: "Inherited" },
  { number: 5, name: "Recognizable situations", control: "Own background · Home situations", tone: "Configurable" },
  { number: 6, name: "Operating areas", control: "Neutral page background + Home dividers", tone: "Inherited" },
  { number: 7, name: "Selected work", control: "Own background · Selected work", tone: "Configurable" },
  { number: 8, name: "Testimonials", control: "Neutral page background", tone: "Inherited" },
  { number: 9, name: "Delivery process", control: "Dark rail + Dark rail accent", tone: "Global token" },
  { number: 10, name: "Engagement models", control: "Full section editor below", tone: "Full control" },
  { number: 11, name: "FAQ", control: "Own background · FAQ", tone: "Configurable" },
  { number: 12, name: "Final CTA", control: "Neutral page background + Home dividers", tone: "Inherited" },
] as const;

const SERVICES_SECTIONS = [
  { number: 1, name: "Hero", control: "Own background · Services hero" },
  { number: 2, name: "Situation selector", control: "Own background · Services situations" },
  { number: 3, name: "Decision map", control: "Own background · Decision map" },
  { number: 4, name: "Service chapters", control: "Own background · Service chapters" },
  { number: 5, name: "Engagement guidance", control: "Neutral surface and semantic accents" },
  { number: 6, name: "PULSE operating model", control: "Dark rail + Dark rail accent" },
] as const;

const THEME_STORAGE_SQL = `alter table public.site_settings
  add column if not exists theme_colors jsonb not null default '{}'::jsonb;

notify pgrst, 'reload schema';`;

function hexToRgb(hex: string) {
  const normalized = /^#[0-9a-f]{6}$/i.test(hex) ? hex.slice(1) : "000000";
  return {
    r: parseInt(normalized.slice(0, 2), 16),
    g: parseInt(normalized.slice(2, 4), 16),
    b: parseInt(normalized.slice(4, 6), 16),
  };
}

function luminance(hex: string) {
  const { r, g, b } = hexToRgb(hex);
  const channels = [r, g, b].map((value) => {
    const channel = value / 255;
    return channel <= 0.03928 ? channel / 12.92 : ((channel + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * channels[0] + 0.7152 * channels[1] + 0.0722 * channels[2];
}

function contrastRatio(a: string, b: string) {
  const first = luminance(a);
  const second = luminance(b);
  const lighter = Math.max(first, second);
  const darker = Math.min(first, second);
  return (lighter + 0.05) / (darker + 0.05);
}

function gradientBackground(angle: ThemeGradientAngle, from: string, to: string) {
  return `linear-gradient(${angle}, ${from}, ${to})`;
}

function ThemePage() {
  const router = useRouter();
  const query = useQuery(siteSettingsQuery());
  const [colors, setColors] = useState<ThemeColors>(DEFAULT_THEME_COLORS);
  const [activeTab, setActiveTab] = useState<EditorTab>("Home");
  const [saved, setSaved] = useState(false);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (query.data?.settings?.theme_colors) {
      setColors(normalizeThemeColors(query.data.settings.theme_colors));
    }
  }, [query.data]);

  const storageReady = query.data?.settings?.theme_storage_ready === true;
  const validation = useMemo(() => ThemeColorsSchema.safeParse(colors), [colors]);
  const contrastChecks = useMemo(() => {
    const checks = [
      { label: "Primary text on white", ratio: contrastRatio(colors.foreground, "#FFFFFF"), minimum: 4.5 },
      { label: "Secondary text on white", ratio: contrastRatio(colors.muted_foreground, "#FFFFFF"), minimum: 4.5 },
      { label: "Primary action", ratio: contrastRatio(colors.primary_action, colors.primary_action_text), minimum: 4.5 },
      { label: "Dark rail accent", ratio: contrastRatio(colors.dark_rail, colors.dark_accent), minimum: 4.5 },
      { label: "Agency card", ratio: contrastRatio(colors.home_engagement_agency_bg, colors.home_engagement_agency_text), minimum: 4.5 },
      { label: "Tasker Army card", ratio: contrastRatio(colors.home_engagement_tasker_bg, colors.home_engagement_tasker_text), minimum: 4.5 },
    ];
    const backgrounds = THEME_BACKGROUND_FIELDS.map((field) => ({
      label: `${field.page} · ${field.label}`,
      ratio: Math.min(
        contrastRatio(colors.foreground, colors[field.from]),
        contrastRatio(colors.foreground, colors[field.to]),
      ),
      minimum: 4.5,
    }));
    return [...checks, ...backgrounds];
  }, [colors]);

  const hasContrastFailure = contrastChecks.some((check) => check.ratio < check.minimum);
  const canSave = storageReady && validation.success && !hasContrastFailure;

  const mutation = useAuthedMutation(updateThemeColors, {
    invalidate: [INVALIDATE.siteSettings],
    invalidateOn: "settled",
    onSuccess: () => {
      setSaved(true);
      setError(null);
      router.invalidate();
      setTimeout(() => setSaved(false), 3000);
    },
    onError: (err) => setError(err.message || "Failed to publish theme settings"),
  });

  const update = <K extends keyof ThemeColors>(key: K, value: ThemeColors[K]) => {
    setSaved(false);
    setColors((current) => ({ ...current, [key]: value }));
  };

  const updateColor = (key: ThemeColorKey, value: string) => update(key, value.toUpperCase());
  const updateAngle = (key: ThemeAngleKey, value: ThemeGradientAngle) => update(key, value);

  const publish = (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    setSaved(false);
    if (!storageReady) {
      setError("Theme storage is not installed. Run the Supabase migration shown above, then check again.");
      return;
    }
    if (!canSave) {
      setError("Fix invalid HEX values and contrast warnings before publishing.");
      return;
    }
    mutation.mutate(colors);
  };

  const copySql = async () => {
    await navigator.clipboard.writeText(THEME_STORAGE_SQL);
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };

  const previewStyle = {
    "--preview-foreground": colors.foreground,
    "--preview-muted": colors.muted_foreground,
    "--preview-border": colors.border,
    "--preview-home-line": colors.home_border,
    "--preview-action": colors.primary_action,
    "--preview-action-text": colors.primary_action_text,
    "--preview-link": colors.link_accent,
    "--preview-editorial": colors.editorial_accent,
    "--preview-dark": colors.dark_rail,
    "--preview-dark-accent": colors.dark_accent,
  } as CSSProperties;

  return (
    <div className="space-y-7">
      <header className="flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">Visual system</p>
          <h1 className="mt-2 font-display text-3xl font-semibold tracking-tight text-foreground">Theme and section editor</h1>
          <p className="mt-2 max-w-3xl text-sm leading-relaxed text-muted-foreground">
            Edit the site by page and named section. Every control states exactly where it appears; money-page recipes show how approved layouts are composed.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <Link to="/" target="_blank" className="inline-flex items-center gap-2 rounded-full border border-border px-4 py-2 text-sm font-medium text-foreground hover:bg-card">
            Open public site <ExternalLink className="h-3.5 w-3.5" />
          </Link>
          <Link to="/admin/settings" className="text-sm text-muted-foreground hover:text-foreground">Back to settings</Link>
        </div>
      </header>

      {!storageReady ? (
        <section className="rounded-2xl border border-amber-600/30 bg-amber-500/10 p-5">
          <div className="grid gap-5 lg:grid-cols-[1fr_auto] lg:items-start">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-amber-800">One-time setup required</p>
              <h2 className="mt-2 font-display text-xl font-semibold text-foreground">Install theme storage in Supabase</h2>
              <p className="mt-2 max-w-3xl text-sm leading-relaxed text-muted-foreground">
                Run the migration, then use “Check again.” Existing defaults remain active until storage is connected.
              </p>
              <p className="mt-3 font-mono text-xs text-foreground">supabase/migrations/20260729171000_dynamic_theme_colors.sql</p>
            </div>
            <div className="flex flex-wrap gap-2">
              <button type="button" onClick={copySql} className="inline-flex items-center gap-2 rounded-full border border-border bg-background px-4 py-2 text-xs font-medium text-foreground hover:bg-card">
                {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                {copied ? "Copied" : "Copy minimal SQL"}
              </button>
              <button type="button" onClick={() => query.refetch()} className="rounded-full bg-foreground px-4 py-2 text-xs font-medium text-background hover:opacity-90">
                Check again
              </button>
            </div>
          </div>
          <pre className="mt-5 overflow-x-auto rounded-xl border border-border bg-background p-4 text-xs leading-relaxed text-foreground"><code>{THEME_STORAGE_SQL}</code></pre>
        </section>
      ) : (
        <div className="flex items-center gap-2 rounded-xl border border-emerald-600/25 bg-emerald-500/10 px-4 py-3 text-sm text-emerald-800">
          <Check className="h-4 w-4" /> Theme storage is connected.
        </div>
      )}

      <section className="rounded-2xl border border-border bg-card/20 p-2">
        <div className="grid gap-2 md:grid-cols-4">
          {TABS.map((tab) => {
            const active = activeTab === tab.value;
            return (
              <button
                key={tab.value}
                type="button"
                onClick={() => setActiveTab(tab.value)}
                className={`rounded-xl px-4 py-3 text-left transition-colors ${active ? "bg-foreground text-background" : "hover:bg-background"}`}
              >
                <span className="block text-sm font-semibold">{tab.label}</span>
                <span className={`mt-1 block text-[11px] leading-relaxed ${active ? "text-background/70" : "text-muted-foreground"}`}>{tab.description}</span>
              </button>
            );
          })}
        </div>
      </section>

      <AdminListShell query={query} skeletonRows={8}>
        {() => (
          <form onSubmit={publish} className="grid gap-8 xl:grid-cols-[minmax(0,1.28fr)_minmax(22rem,0.72fr)]">
            <div className="space-y-7">
              {activeTab === "Home" ? (
                <HomePanel colors={colors} validation={validation} update={update} updateColor={updateColor} updateAngle={updateAngle} />
              ) : null}
              {activeTab === "Services" ? (
                <ServicesPanel colors={colors} validation={validation} updateColor={updateColor} updateAngle={updateAngle} />
              ) : null}
              {activeTab === "Money pages" ? (
                <MoneyPagesPanel colors={colors} setColors={setColors} />
              ) : null}
              {activeTab === "Global" ? (
                <GlobalPanel colors={colors} validation={validation} updateColor={updateColor} setColors={setColors} />
              ) : null}
            </div>

            <aside className="space-y-5 xl:sticky xl:top-6 xl:self-start" style={previewStyle}>
              <ThemePreview activeTab={activeTab} colors={colors} />

              <section className="rounded-2xl border border-border bg-card/30 p-5">
                <h2 className="font-display text-lg font-semibold text-foreground">Contrast checks</h2>
                <div className="mt-4 max-h-64 space-y-3 overflow-y-auto pr-1">
                  {contrastChecks.map((check) => {
                    const passes = check.ratio >= check.minimum;
                    return (
                      <div key={check.label} className="flex items-center justify-between gap-4 text-xs">
                        <span className="text-muted-foreground">{check.label}</span>
                        <span className={passes ? "font-medium text-emerald-700" : "font-medium text-destructive"}>
                          {passes ? "Pass" : "Fail"} · {check.ratio.toFixed(2)}:1
                        </span>
                      </div>
                    );
                  })}
                </div>
              </section>

              {error ? <div className="rounded-xl border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive">{error}</div> : null}
              {saved ? (
                <div className="flex items-start gap-2 rounded-xl border border-emerald-600/25 bg-emerald-500/10 px-4 py-3 text-sm text-emerald-800">
                  <Check className="mt-0.5 h-4 w-4 shrink-0" />
                  <span>Published. Public pages pick up the new settings within 5 minutes.</span>
                </div>
              ) : null}

              <button
                type="submit"
                disabled={mutation.isPending || !canSave}
                className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-foreground px-5 py-3 text-sm font-semibold text-background transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40"
              >
                {mutation.isPending ? "Publishing…" : "Publish theme"}
                <ArrowRight className="h-4 w-4" />
              </button>
              <p className="text-center text-[11px] leading-relaxed text-muted-foreground">
                Layout controls are limited to the named engagement section. Copy, routes, claims and SEO ownership are unchanged.
              </p>
            </aside>
          </form>
        )}
      </AdminListShell>
    </div>
  );
}

function HomePanel({
  colors,
  validation,
  update,
  updateColor,
  updateAngle,
}: {
  colors: ThemeColors;
  validation: ReturnType<typeof ThemeColorsSchema.safeParse>;
  update: <K extends keyof ThemeColors>(key: K, value: ThemeColors[K]) => void;
  updateColor: (key: ThemeColorKey, value: string) => void;
  updateAngle: (key: ThemeAngleKey, value: ThemeGradientAngle) => void;
}) {
  const homeBackgrounds = THEME_BACKGROUND_FIELDS.filter((field) => field.page === "Home");

  return (
    <>
      <section className="overflow-hidden rounded-2xl border border-border bg-card/20">
        <header className="border-b border-border px-5 py-4">
          <div className="flex items-center gap-3">
            <ListTree className="h-5 w-5 text-muted-foreground" />
            <div>
              <h2 className="font-display text-lg font-semibold text-foreground">Home section map</h2>
              <p className="mt-1 text-xs text-muted-foreground">The public order is shown here. No more guessing from “section 5” or a generic blue token.</p>
            </div>
          </div>
        </header>
        <div className="grid gap-px bg-border sm:grid-cols-2 lg:grid-cols-3">
          {HOME_SECTIONS.map((section) => (
            <article key={section.number} className="bg-background p-4">
              <div className="flex items-start justify-between gap-3">
                <span className="font-mono text-[10px] text-muted-foreground">{String(section.number).padStart(2, "0")}</span>
                <span className="rounded-full border border-border px-2 py-1 text-[9px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">{section.tone}</span>
              </div>
              <h3 className="mt-3 text-sm font-semibold text-foreground">{section.name}</h3>
              <p className="mt-1 text-[11px] leading-relaxed text-muted-foreground">{section.control}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="rounded-2xl border border-border bg-card/20">
        <header className="border-b border-border px-5 py-4">
          <h2 className="font-display text-lg font-semibold text-foreground">Home page system</h2>
          <p className="mt-1 text-xs text-muted-foreground">This is the control for the pale blue dividers that previously had no obvious owner.</p>
        </header>
        <div className="grid gap-4 p-5 md:grid-cols-[minmax(0,1fr)_13rem] md:items-center">
          <div>
            <label htmlFor="theme-home-border" className="text-sm font-semibold text-foreground">Home dividers and hairlines</label>
            <p className="mt-1 text-xs leading-relaxed text-muted-foreground">Borders inside Home sections, excluding the dark process rail and the independently controlled engagement cards.</p>
            <p className="mt-2 font-mono text-[10px] text-muted-foreground">Affects: Home → former border-blue-* lines</p>
          </div>
          <ColorControl id="theme-home-border" label="Home dividers" value={colors.home_border} onChange={(value) => updateColor("home_border", value)} error={fieldError(validation, "home_border")} />
        </div>
        <div className="border-t border-border p-5">
          <SelectControl
            label="Home gradient direction"
            description="Shared direction for Home surfaces. Equal start and end colors still produce a solid background."
            value={colors.home_gradient_angle}
            onChange={(value) => updateAngle("home_gradient_angle", value as ThemeGradientAngle)}
            options={THEME_GRADIENT_ANGLES}
          />
        </div>
      </section>

      <BackgroundPanel page="Home" fields={homeBackgrounds} colors={colors} validation={validation} updateColor={updateColor} />

      <section id="home-engagement-editor" className="overflow-hidden rounded-2xl border-2 border-foreground/15 bg-card/20">
        <header className="border-b border-border bg-background/60 px-5 py-5">
          <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">Home · Section 10</p>
          <h2 className="mt-2 font-display text-xl font-semibold text-foreground">Two engagement models</h2>
          <p className="mt-1 max-w-3xl text-xs leading-relaxed text-muted-foreground">
            Controls the full section background, whether the section and cards have borders, and whether the choices appear as editorial rows, independent cards or one split panel.
          </p>
        </header>

        <div className="grid gap-6 p-5 lg:grid-cols-2">
          <SelectControl
            label="Presentation"
            description={THEME_ENGAGEMENT_LAYOUTS.find((item) => item.value === colors.home_engagement_layout)?.description ?? ""}
            value={colors.home_engagement_layout}
            onChange={(value) => update("home_engagement_layout", value as ThemeColors["home_engagement_layout"])}
            options={THEME_ENGAGEMENT_LAYOUTS}
            icon={colors.home_engagement_layout === "editorial" ? Rows3 : colors.home_engagement_layout === "cards" ? LayoutGrid : SquareSplitHorizontal}
          />
          <SelectControl
            label="Section vertical spacing"
            description="Changes only the outer breathing room of this section."
            value={colors.home_engagement_spacing}
            onChange={(value) => update("home_engagement_spacing", value as ThemeColors["home_engagement_spacing"])}
            options={THEME_ENGAGEMENT_SPACING}
          />
          <SelectControl
            label="Card corner radius"
            description="Used by Cards and Split panel presentations."
            value={colors.home_engagement_radius}
            onChange={(value) => update("home_engagement_radius", value as ThemeColors["home_engagement_radius"])}
            options={THEME_ENGAGEMENT_RADII}
          />
          <SelectControl
            label="Gap between choices"
            description="Used by Cards; on mobile it also separates stacked choices."
            value={colors.home_engagement_gap}
            onChange={(value) => update("home_engagement_gap", value as ThemeColors["home_engagement_gap"])}
            options={THEME_ENGAGEMENT_GAPS}
          />
        </div>

        <div className="grid gap-px border-y border-border bg-border sm:grid-cols-2">
          <ToggleControl
            label="Section borders"
            description="Top and bottom border around the complete engagement section."
            checked={colors.home_engagement_section_border}
            onChange={(value) => update("home_engagement_section_border", value)}
          />
          <ToggleControl
            label="Choice borders"
            description="Borders around cards, split panels or the editorial choice rail."
            checked={colors.home_engagement_card_borders}
            onChange={(value) => update("home_engagement_card_borders", value)}
          />
        </div>

        <div className="grid gap-px bg-border lg:grid-cols-2">
          <CardColorGroup
            title="Agency choice"
            subtitle="Project-based engagements"
            colors={[
              { key: "home_engagement_agency_bg", label: "Background" },
              { key: "home_engagement_agency_text", label: "Text" },
              { key: "home_engagement_agency_border", label: "Border" },
            ]}
            values={colors}
            validation={validation}
            updateColor={updateColor}
          />
          <CardColorGroup
            title="Tasker Army choice"
            subtitle="Ongoing engineering"
            colors={[
              { key: "home_engagement_tasker_bg", label: "Background" },
              { key: "home_engagement_tasker_text", label: "Text" },
              { key: "home_engagement_tasker_border", label: "Border" },
            ]}
            values={colors}
            validation={validation}
            updateColor={updateColor}
          />
        </div>
      </section>
    </>
  );
}

function ServicesPanel({
  colors,
  validation,
  updateColor,
  updateAngle,
}: {
  colors: ThemeColors;
  validation: ReturnType<typeof ThemeColorsSchema.safeParse>;
  updateColor: (key: ThemeColorKey, value: string) => void;
  updateAngle: (key: ThemeAngleKey, value: ThemeGradientAngle) => void;
}) {
  const servicesBackgrounds = THEME_BACKGROUND_FIELDS.filter((field) => field.page === "Services");
  return (
    <>
      <section className="overflow-hidden rounded-2xl border border-border bg-card/20">
        <header className="border-b border-border px-5 py-4">
          <h2 className="font-display text-lg font-semibold text-foreground">Services section map</h2>
          <p className="mt-1 text-xs text-muted-foreground">The map mirrors the public Services page order.</p>
        </header>
        <div className="grid gap-px bg-border sm:grid-cols-2 lg:grid-cols-3">
          {SERVICES_SECTIONS.map((section) => (
            <article key={section.number} className="bg-background p-4">
              <span className="font-mono text-[10px] text-muted-foreground">{String(section.number).padStart(2, "0")}</span>
              <h3 className="mt-3 text-sm font-semibold text-foreground">{section.name}</h3>
              <p className="mt-1 text-[11px] leading-relaxed text-muted-foreground">{section.control}</p>
            </article>
          ))}
        </div>
      </section>
      <section className="rounded-2xl border border-border bg-card/20 p-5">
        <SelectControl
          label="Services gradient direction"
          description="Shared direction for the four configurable Services surfaces."
          value={colors.services_gradient_angle}
          onChange={(value) => updateAngle("services_gradient_angle", value as ThemeGradientAngle)}
          options={THEME_GRADIENT_ANGLES}
        />
      </section>
      <BackgroundPanel page="Services" fields={servicesBackgrounds} colors={colors} validation={validation} updateColor={updateColor} />
    </>
  );
}

function MoneyPagesPanel({ colors, setColors }: { colors: ThemeColors; setColors: (colors: ThemeColors) => void }) {
  const plusPreset = THEME_PRESETS.find((preset) => preset.name === "Shopify Plus");
  return (
    <>
      <section className="rounded-2xl border border-border bg-card/20 p-5">
        <div className="flex items-start gap-4">
          <MonitorCog className="mt-0.5 h-5 w-5 text-muted-foreground" />
          <div>
            <h2 className="font-display text-lg font-semibold text-foreground">How money pages are configured</h2>
            <p className="mt-2 max-w-3xl text-sm leading-relaxed text-muted-foreground">
              A money page is not reconstructed section by section in this editor. Its route selects an approved React template; the theme editor controls the semantic tokens that template consumes.
            </p>
          </div>
        </div>
      </section>

      <section className="overflow-hidden rounded-2xl border-2 border-[#0a0a0a]/25 bg-card/20">
        <div className="grid gap-6 p-6 lg:grid-cols-[1fr_auto] lg:items-start">
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-[#1f1f1f]">Exact recipe</p>
            <h2 className="mt-2 font-display text-2xl font-semibold text-foreground">Shopify Plus agency</h2>
            <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
              Route <code>/shopify-plus-agency</code> renders <code>EnterpriseMoneyPage</code>. Replicating it means reusing that component, not copying the Home colors by eye.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Link to="/shopify-plus-agency" target="_blank" className="inline-flex items-center gap-2 rounded-full border border-border px-4 py-2 text-xs font-semibold text-foreground">
              Open page <ExternalLink className="h-3.5 w-3.5" />
            </Link>
            <button
              type="button"
              onClick={() => plusPreset && setColors({ ...plusPreset.colors })}
              className="rounded-full bg-foreground px-4 py-2 text-xs font-semibold text-background"
            >
              Apply Shopify Plus recipe
            </button>
          </div>
        </div>
        <div className="grid gap-px border-t border-border bg-border md:grid-cols-3">
          <RecipeCell label="Template" value="EnterpriseMoneyPage" detail="Architecture, evidence, boundaries, engagement path, resources and FAQ." />
          <RecipeCell label="Light-surface accent" value="Editorial bronze · #1f1f1f" detail="Eyebrows, numbers, checks and restrained dividers." />
          <RecipeCell label="Enterprise rail" value="Navy · #0a0a0a" detail="Evidence band with a light bronze or violet accent." />
          <RecipeCell label="Platform lines" value="Platform accent" detail="Capability panel and cool evidence surfaces." />
          <RecipeCell label="Neutral system" value="Foreground + neutral border" detail="Tables, rows, buttons and typography hierarchy." />
          <RecipeCell label="Replication rule" value="Reuse the component" detail="Create a route and supply governed copy/schema; do not duplicate JSX." />
        </div>
      </section>

      <section className="rounded-2xl border border-border bg-card/20">
        <header className="border-b border-border px-5 py-4">
          <h2 className="font-display text-lg font-semibold text-foreground">Approved template families</h2>
          <p className="mt-1 text-xs text-muted-foreground">These cards explain which global token group changes each family.</p>
        </header>
        <div className="divide-y divide-border">
          <TemplateRow title="Technical editorial" routes="Performance · Technical SEO" tokens="Growth accent or SEO accent + neutral rows" />
          <TemplateRow title="Architecture-first" routes="Development · CRO · Integrations · Apps · Analytics" tokens="Service accent + soft surface + line" />
          <TemplateRow title="Enterprise" routes="Shopify Plus" tokens="Editorial bronze + platform lines + dark rail" />
          <TemplateRow title="Operating model" routes="Retainers" tokens="Neutral system + editorial evidence accents" />
          <TemplateRow title="Buying decision" routes="Pricing" tokens="Neutral system + controlled tier emphasis" />
        </div>
      </section>

      <section className="rounded-2xl border border-border bg-card/20 p-5">
        <h2 className="font-display text-lg font-semibold text-foreground">Current Shopify Plus token values</h2>
        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          <TokenReadout label="Editorial accent" value={colors.editorial_accent} />
          <TokenReadout label="Dark rail" value={colors.dark_rail} />
          <TokenReadout label="Dark accent" value={colors.dark_accent} />
          <TokenReadout label="Platform accent" value={colors.platform_accent} />
          <TokenReadout label="Neutral border" value={colors.border} />
          <TokenReadout label="Primary action" value={colors.primary_action} />
        </div>
      </section>
    </>
  );
}

function GlobalPanel({
  colors,
  validation,
  updateColor,
  setColors,
}: {
  colors: ThemeColors;
  validation: ReturnType<typeof ThemeColorsSchema.safeParse>;
  updateColor: (key: ThemeColorKey, value: string) => void;
  setColors: (colors: ThemeColors) => void;
}) {
  const groups = ["Foundation", "Actions", "Service accents", "Editorial surfaces"] as const;
  return (
    <>
      <section className="rounded-2xl border border-border bg-card/20 p-5">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h2 className="font-display text-lg font-semibold text-foreground">Presets</h2>
            <p className="mt-1 text-xs text-muted-foreground">Presets change the local draft only. Use Publish theme to make them public.</p>
          </div>
          <div className="flex flex-wrap gap-2">
            {THEME_PRESETS.map((preset) => (
              <button
                key={preset.name}
                type="button"
                title={preset.description}
                onClick={() => setColors({ ...preset.colors })}
                className="rounded-full border border-border bg-background px-4 py-2 text-xs font-medium text-foreground hover:border-foreground/40"
              >
                {preset.name}
              </button>
            ))}
            <button
              type="button"
              onClick={() => setColors({ ...DEFAULT_THEME_COLORS })}
              className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs font-medium text-muted-foreground hover:text-foreground"
            >
              <RotateCcw className="h-3.5 w-3.5" /> Reset approved defaults
            </button>
          </div>
        </div>
      </section>

      {groups.map((group) => (
        <section key={group} className="rounded-2xl border border-border bg-card/20">
          <header className="border-b border-border px-5 py-4">
            <h2 className="font-display text-lg font-semibold text-foreground">{group}</h2>
          </header>
          <div className="divide-y divide-border">
            {THEME_COLOR_FIELDS.filter((field) => field.group === group).map((field) => (
              <div key={field.key} className="grid gap-4 p-5 md:grid-cols-[minmax(0,1fr)_13rem] md:items-center">
                <div>
                  <label htmlFor={`theme-${field.key}`} className="text-sm font-semibold text-foreground">{field.label}</label>
                  <p className="mt-1 text-xs leading-relaxed text-muted-foreground">{field.description}</p>
                  <p className="mt-2 font-mono text-[10px] text-muted-foreground">Affects: {field.usedBy}</p>
                </div>
                <ColorControl id={`theme-${field.key}`} label={field.label} value={colors[field.key]} onChange={(value) => updateColor(field.key, value)} error={fieldError(validation, field.key)} />
              </div>
            ))}
          </div>
        </section>
      ))}
    </>
  );
}

function BackgroundPanel({
  page,
  fields,
  colors,
  validation,
  updateColor,
}: {
  page: "Home" | "Services";
  fields: typeof THEME_BACKGROUND_FIELDS;
  colors: ThemeColors;
  validation: ReturnType<typeof ThemeColorsSchema.safeParse>;
  updateColor: (key: ThemeColorKey, value: string) => void;
}) {
  return (
    <section className="rounded-2xl border border-border bg-card/20">
      <header className="border-b border-border px-5 py-4">
        <h2 className="font-display text-lg font-semibold text-foreground">{page} backgrounds</h2>
        <p className="mt-1 text-xs text-muted-foreground">Set both values to the same color for a solid surface. Different values create a gradient.</p>
      </header>
      <div className="divide-y divide-border">
        {fields.map((field) => (
          <div key={`${field.page}-${field.label}`} className="grid gap-4 p-5 lg:grid-cols-[minmax(0,1fr)_minmax(18rem,0.9fr)] lg:items-center">
            <div>
              <div className="flex items-center gap-3">
                <span className="font-mono text-[10px] text-muted-foreground">{String(field.section).padStart(2, "0")}</span>
                <h3 className="text-sm font-semibold text-foreground">{field.label}</h3>
              </div>
              <p className="mt-1 text-xs leading-relaxed text-muted-foreground">{field.description}</p>
              <p className="mt-2 font-mono text-[10px] text-muted-foreground">Affects: {field.usedBy}</p>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <ColorControl id={`theme-${field.from}`} label={`${field.label} start`} value={colors[field.from]} onChange={(value) => updateColor(field.from, value)} error={fieldError(validation, field.from)} compact />
              <ColorControl id={`theme-${field.to}`} label={`${field.label} end`} value={colors[field.to]} onChange={(value) => updateColor(field.to, value)} error={fieldError(validation, field.to)} compact />
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function CardColorGroup({
  title,
  subtitle,
  colors,
  values,
  validation,
  updateColor,
}: {
  title: string;
  subtitle: string;
  colors: ReadonlyArray<{ key: ThemeColorKey; label: string }>;
  values: ThemeColors;
  validation: ReturnType<typeof ThemeColorsSchema.safeParse>;
  updateColor: (key: ThemeColorKey, value: string) => void;
}) {
  return (
    <div className="bg-background p-5">
      <h3 className="font-display text-lg font-semibold text-foreground">{title}</h3>
      <p className="mt-1 text-xs text-muted-foreground">{subtitle}</p>
      <div className="mt-5 space-y-4">
        {colors.map((color) => (
          <div key={color.key}>
            <label htmlFor={`theme-${color.key}`} className="mb-1.5 block text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">{color.label}</label>
            <ColorControl id={`theme-${color.key}`} label={`${title} ${color.label}`} value={values[color.key]} onChange={(value) => updateColor(color.key, value)} error={fieldError(validation, color.key)} />
          </div>
        ))}
      </div>
    </div>
  );
}

function ThemePreview({ activeTab, colors }: { activeTab: EditorTab; colors: ThemeColors }) {
  if (activeTab === "Home") return <EngagementPreview colors={colors} />;
  if (activeTab === "Services") {
    return (
      <section className="overflow-hidden rounded-2xl border border-[var(--preview-border)] bg-white shadow-sm">
        <div className="p-6" style={{ background: gradientBackground(colors.services_gradient_angle, colors.services_hero_from, colors.services_hero_to) }}>
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[var(--preview-editorial)]">Services hero</p>
          <h2 className="mt-3 font-display text-2xl font-semibold text-[var(--preview-foreground)]">Choose the constraint before the service.</h2>
          <p className="mt-3 text-sm text-[var(--preview-muted)]">This preview uses the selected Services surface and global text tokens.</p>
        </div>
        <div className="border-t border-[var(--preview-border)] p-5" style={{ background: gradientBackground(colors.services_gradient_angle, colors.services_situations_from, colors.services_situations_to) }}>
          <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-[var(--preview-editorial)]">Situation selector</p>
          <p className="mt-2 text-sm font-semibold text-[var(--preview-foreground)]">What is Shopify preventing your team from doing?</p>
        </div>
      </section>
    );
  }
  if (activeTab === "Money pages") {
    return (
      <section className="overflow-hidden rounded-[1.75rem] border border-[var(--preview-border)] bg-white shadow-sm">
        <div className="border-b border-[var(--preview-border)] p-6">
          <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-[var(--preview-editorial)]">Shopify Plus engineering</p>
          <h2 className="mt-3 font-display text-2xl font-semibold text-[var(--preview-foreground)]">Enterprise capability architecture.</h2>
          <p className="mt-3 text-sm leading-relaxed text-[var(--preview-muted)]">Bronze evidence signals, platform lines, neutral tables and a dark enterprise rail.</p>
        </div>
        <div className="bg-[var(--preview-dark)] p-6 text-white">
          <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-[var(--preview-dark-accent)]">Delivery record · not a forecast</p>
          <p className="mt-3 text-sm leading-relaxed text-white/75">Experience indicators remain separate from the forecast for a new engagement.</p>
        </div>
      </section>
    );
  }
  return (
    <section className="overflow-hidden rounded-2xl border border-[var(--preview-border)] bg-white shadow-sm">
      <div className="p-6">
        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[var(--preview-editorial)]">Global system</p>
        <h2 className="mt-3 font-display text-3xl font-semibold leading-tight text-[var(--preview-foreground)]">Semantic tokens keep pages coherent.</h2>
        <p className="mt-4 text-sm leading-relaxed text-[var(--preview-muted)]">Primary and secondary text, actions, links and borders update across controlled templates.</p>
        <div className="mt-6 flex flex-wrap items-center gap-4">
          <span className="rounded-full bg-[var(--preview-action)] px-5 py-2.5 text-sm font-medium text-[var(--preview-action-text)]">Primary action</span>
          <span className="text-sm font-medium text-[var(--preview-link)]">Selective link →</span>
        </div>
      </div>
    </section>
  );
}

function EngagementPreview({ colors }: { colors: ThemeColors }) {
  const isEditorial = colors.home_engagement_layout === "editorial";
  const isSplit = colors.home_engagement_layout === "split";
  const gap = isSplit ? "0px" : colors.home_engagement_gap;
  const sectionBorder = colors.home_engagement_section_border ? `1px solid ${colors.home_border}` : "0 solid transparent";
  const cardBorderWidth = colors.home_engagement_card_borders ? "1px" : "0px";
  const background = gradientBackground(colors.home_gradient_angle, colors.home_engagement_from, colors.home_engagement_to);

  return (
    <section className="overflow-hidden rounded-2xl shadow-sm" style={{ background, borderTop: sectionBorder, borderBottom: sectionBorder }}>
      <div className="p-6">
        <p className="text-[10px] font-semibold uppercase tracking-[0.18em]" style={{ color: colors.editorial_accent }}>How we engage</p>
        <h2 className="mt-3 font-display text-2xl font-semibold" style={{ color: colors.foreground }}>Two engagement models. One senior team.</h2>
        <p className="mt-3 text-sm leading-relaxed" style={{ color: colors.muted_foreground }}>Choose a defined agency scope or continuous Tasker Army delivery.</p>
      </div>
      {isEditorial ? (
        <div className="px-6 pb-6">
          {[
            ["Agency", "Project-based work"],
            ["Tasker Army", "Ongoing engineering"],
          ].map((item, index) => (
            <div key={item[0]} className="flex items-center justify-between gap-4 py-4" style={{ borderTop: `${cardBorderWidth} solid ${colors.home_border}`, borderBottom: index === 1 ? `${cardBorderWidth} solid ${colors.home_border}` : undefined }}>
              <div>
                <p className="font-display text-base font-semibold" style={{ color: colors.foreground }}>{item[0]}</p>
                <p className="mt-1 text-xs" style={{ color: colors.muted_foreground }}>{item[1]}</p>
              </div>
              <ArrowRight className="h-4 w-4" style={{ color: colors.muted_foreground }} />
            </div>
          ))}
        </div>
      ) : (
        <div className="grid px-6 pb-6 sm:grid-cols-2" style={{ gap }}>
          <PreviewChoice
            title="Agency"
            body="Project-based engagements."
            background={colors.home_engagement_agency_bg}
            color={colors.home_engagement_agency_text}
            border={colors.home_engagement_agency_border}
            borderWidth={cardBorderWidth}
            radius={isSplit ? `${colors.home_engagement_radius} 0 0 ${colors.home_engagement_radius}` : colors.home_engagement_radius}
          />
          <PreviewChoice
            title="Tasker Army"
            body="Ongoing engineering."
            background={colors.home_engagement_tasker_bg}
            color={colors.home_engagement_tasker_text}
            border={colors.home_engagement_tasker_border}
            borderWidth={cardBorderWidth}
            radius={isSplit ? `0 ${colors.home_engagement_radius} ${colors.home_engagement_radius} 0` : colors.home_engagement_radius}
          />
        </div>
      )}
    </section>
  );
}

function PreviewChoice({
  title,
  body,
  background,
  color,
  border,
  borderWidth,
  radius,
}: {
  title: string;
  body: string;
  background: string;
  color: string;
  border: string;
  borderWidth: string;
  radius: string;
}) {
  return (
    <article className="min-h-40 p-5" style={{ background, color, border: `${borderWidth} solid ${border}`, borderRadius: radius }}>
      <h3 className="font-display text-lg font-semibold">{title}</h3>
      <p className="mt-2 text-xs opacity-75">{body}</p>
      <ArrowRight className="mt-8 h-4 w-4" />
    </article>
  );
}

function SelectControl({
  label,
  description,
  value,
  onChange,
  options,
  icon: Icon,
}: {
  label: string;
  description: string;
  value: string;
  onChange: (value: string) => void;
  options: ReadonlyArray<{ value: string; label: string; description?: string }>;
  icon?: typeof Rows3;
}) {
  return (
    <label className="block">
      <span className="flex items-center gap-2 text-sm font-semibold text-foreground">{Icon ? <Icon className="h-4 w-4 text-muted-foreground" /> : null}{label}</span>
      <span className="mt-1 block min-h-8 text-xs leading-relaxed text-muted-foreground">{description}</span>
      <select value={value} onChange={(event) => onChange(event.target.value)} className="mt-3 w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground">
        {options.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
      </select>
    </label>
  );
}

function ToggleControl({
  label,
  description,
  checked,
  onChange,
}: {
  label: string;
  description: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}) {
  return (
    <label className="flex cursor-pointer items-start justify-between gap-5 bg-background p-5">
      <span>
        <span className="block text-sm font-semibold text-foreground">{label}</span>
        <span className="mt-1 block text-xs leading-relaxed text-muted-foreground">{description}</span>
      </span>
      <span className={`relative mt-0.5 h-6 w-11 shrink-0 rounded-full transition-colors ${checked ? "bg-foreground" : "bg-muted"}`}>
        <input type="checkbox" checked={checked} onChange={(event) => onChange(event.target.checked)} className="sr-only" />
        <span className={`absolute top-1 h-4 w-4 rounded-full bg-background transition-transform ${checked ? "translate-x-6" : "translate-x-1"}`} />
      </span>
    </label>
  );
}

function RecipeCell({ label, value, detail }: { label: string; value: string; detail: string }) {
  return (
    <div className="bg-background p-5">
      <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">{label}</p>
      <p className="mt-2 text-sm font-semibold text-foreground">{value}</p>
      <p className="mt-2 text-xs leading-relaxed text-muted-foreground">{detail}</p>
    </div>
  );
}

function TemplateRow({ title, routes, tokens }: { title: string; routes: string; tokens: string }) {
  return (
    <div className="grid gap-3 p-5 md:grid-cols-[0.65fr_1fr_1fr] md:items-center">
      <h3 className="text-sm font-semibold text-foreground">{title}</h3>
      <p className="text-xs text-muted-foreground">{routes}</p>
      <p className="font-mono text-[10px] leading-relaxed text-muted-foreground">{tokens}</p>
    </div>
  );
}

function TokenReadout({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between gap-4 rounded-xl border border-border bg-background px-4 py-3">
      <span className="text-xs text-muted-foreground">{label}</span>
      <span className="flex items-center gap-2 font-mono text-xs text-foreground"><span className="h-4 w-4 rounded border border-border" style={{ background: value }} />{value}</span>
    </div>
  );
}

function fieldError(validation: ReturnType<typeof ThemeColorsSchema.safeParse>, key: ThemeColorKey) {
  return validation.success ? null : validation.error.issues.find((issue) => issue.path[0] === key)?.message;
}

function ColorControl({
  id,
  label,
  value,
  onChange,
  error,
  compact = false,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  error?: string | null;
  compact?: boolean;
}) {
  return (
    <div>
      {compact ? <label htmlFor={id} className="mb-1 block text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">{label.includes(" start") ? "Start" : "End"}</label> : null}
      <div className="flex items-center gap-2">
        <input
          aria-label={`${label} picker`}
          type="color"
          value={/^#[0-9a-f]{6}$/i.test(value) ? value : "#000000"}
          onChange={(event) => onChange(event.target.value)}
          className="h-10 w-12 cursor-pointer rounded-md border border-border bg-background p-1"
        />
        <input
          id={id}
          type="text"
          value={value}
          onChange={(event) => onChange(event.target.value)}
          spellCheck={false}
          className="min-w-0 flex-1 rounded-md border border-border bg-background px-3 py-2 font-mono text-sm uppercase text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
        />
      </div>
      {error ? <p className="mt-1 text-xs text-destructive">{error}</p> : null}
    </div>
  );
}
